import React, { useEffect, useMemo } from "react";

import {
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    Divider
} from "@mui/material";

import {
    ResponsiveContainer,
    AreaChart,
    Area,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar
} from "recharts";

import SavingsIcon from "@mui/icons-material/Savings";
import HomeIcon from "@mui/icons-material/Home";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "motion/react";


const COLORS = ["#4caf50", "#ef5350"];


const GraficosSeguimientos = () => {

    // =========================================================
    // NAVEGACIÓN
    // =========================================================

    const location = useLocation();
    const navigate = useNavigate();

    const { abonos, rendimiento } = location.state || {};


    useEffect(() => {

        if (!abonos || rendimiento == null) {
            navigate("/home-clientes", {
                replace: true
            });
        }

    }, [abonos, rendimiento, navigate]);


    // =========================================================
    // PROCESAMIENTO DE INFORMACIÓN
    // =========================================================

    const {
        datos,
        progreso,
        pie,
        saldoAhorrado,
        meta,
        pagados,
        pendientes,
        ganancia,
        totalFinal
    } = useMemo(() => {

        if (!Array.isArray(abonos) || rendimiento == null) {

            return {
                datos: [],
                progreso: [],
                pie: [
                    {
                        name: "Pagados",
                        value: 0
                    },
                    {
                        name: "Pendientes",
                        value: 0
                    }
                ],
                saldoAhorrado: 0,
                meta: 0,
                pagados: 0,
                pendientes: 0,
                ganancia: 0,
                totalFinal: 0
            };

        }


        let acumulado = 0;


        const datos = abonos.map(item => {

            const cantidad = Number(item.cantidad);


            if (item.ahorro_pagado) {
                acumulado += cantidad;
            }


            return {
                ...item,
                cantidad,

                fecha: new Date(item.fecha_pago).toLocaleDateString(
                    "es-MX",
                    {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                    }
                ),

                acumulado
            };

        });


        const pagados = datos.filter(
            x => x.ahorro_pagado
        ).length;


        const pendientes = datos.length - pagados;


        const saldoAhorrado = datos
            .filter(x => x.ahorro_pagado)
            .reduce(
                (a, b) => a + b.cantidad,
                0
            );


        const meta = datos.reduce(
            (a, b) => a + b.cantidad,
            0
        );


        const ganancia = meta * rendimiento;


        const totalFinal = meta + ganancia;


        return {

            datos,

            progreso: datos,

            saldoAhorrado,

            meta,

            pagados,

            pendientes,

            ganancia,

            totalFinal,

            pie: [
                {
                    name: "Pagados",
                    value: pagados
                },
                {
                    name: "Pendientes",
                    value: pendientes
                }
            ]

        };

    }, [abonos, rendimiento]);


    // =========================================================
    // FUNCIONES
    // =========================================================

    const regresarHome = () => {

        navigate("/home-clientes");

    };


    const formatoMoneda = valor => {

        return `$${Number(valor).toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;

    };


    // =========================================================
    // ESTILO GENERAL DE TARJETAS
    // =========================================================

    const cardSX = {

        height: "100%",

        borderRadius: 3,

        border: "1px solid",

        borderColor: "divider",

        boxShadow:
            "0 4px 18px rgba(0,0,0,0.06)",

        transition:
            "all 0.25s ease",

        "&:hover": {

            boxShadow:
                "0 8px 25px rgba(0,0,0,0.10)"

        }

    };


    return (

        <Box

            component={motion.div}

            initial={{
                opacity: 0,
                y: -40
            }}

            animate={{
                opacity: 1,
                y: 0
            }}

            transition={{
                duration: 0.6,
                easing: "ease-out"
            }}

            sx={{

                minHeight: "100vh",

                backgroundColor: "#f5f7fb",

                px: {
                    xs: 2,
                    md: 4
                },

                py: 3

            }}

        >


            {/* =================================================
                HEADER
            ================================================= */}

            <Box

                sx={{

                    display: "flex",

                    flexDirection: {
                        xs: "column",
                        sm: "row"
                    },

                    justifyContent:
                        "space-between",

                    alignItems: {
                        xs: "stretch",
                        sm: "center"
                    },

                    gap: 2,

                    mb: 4

                }}

            >


                <Box

                    sx={{

                        display: "flex",

                        alignItems:
                            "center",

                        gap: 2

                    }}

                >


                    <Box

                        sx={{

                            width: 58,

                            height: 58,

                            borderRadius: 3,

                            display: "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "center",

                            background:
                                "linear-gradient(135deg, #2E7D32, #43A047)",

                            color: "white",

                            boxShadow:
                                "0 6px 18px rgba(46,125,50,0.25)"

                        }}

                    >

                        <SavingsIcon
                            sx={{
                                fontSize: 32
                            }}
                        />

                    </Box>


                    <Box>

                        <Typography

                            variant="h4"

                            fontWeight="bold"

                            sx={{

                                fontSize: {

                                    xs: "1.6rem",

                                    md: "2.1rem"

                                }

                            }}

                        >

                            Seguimiento de Ahorro

                        </Typography>


                        <Typography
                            color="text.secondary"
                        >

                            Consulta el avance de tus
                            aportaciones.

                        </Typography>

                    </Box>

                </Box>


                {/* BOTÓN HOME */}

                <Button

                    component={motion.button}

                    whileHover={{
                        scale: 1.05
                    }}

                    whileTap={{
                        scale: 0.96
                    }}

                    variant="outlined"

                    color="primary"

                    startIcon={
                        <HomeIcon />
                    }

                    onClick={
                        regresarHome
                    }

                    sx={{

                        width: {
                            xs: "100%",
                            sm: "auto"
                        },

                        borderRadius: 2,

                        px: 2.5,

                        py: 1,

                        fontWeight: 600

                    }}

                >

                    Regresar a Home

                </Button>


            </Box>


            <Grid container spacing={3}>


                {/* =================================================
                    KPI - AHORRADO
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        sm: 6,
                        md: 3
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95
                    }}

                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                    }}

                    transition={{
                        duration: 0.45,
                        delay: 0.15,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box

                                sx={{

                                    display: "flex",

                                    justifyContent:
                                        "space-between",

                                    alignItems:
                                        "flex-start"

                                }}

                            >

                                <Box>

                                    <Typography

                                        color="text.secondary"

                                        fontSize={14}

                                        fontWeight={500}

                                    >

                                        Ahorrado

                                    </Typography>


                                    <Typography

                                        variant="h4"

                                        fontWeight={700}

                                        sx={{
                                            mt: 1
                                        }}

                                    >

                                        {
                                            formatoMoneda(
                                                saldoAhorrado
                                            )
                                        }

                                    </Typography>

                                </Box>


                                <AccountBalanceWalletIcon

                                    sx={{

                                        fontSize: 34,

                                        color:
                                            "success.main"

                                    }}

                                />

                            </Box>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                                sx={{
                                    mt: 2
                                }}

                            >

                                Monto acumulado

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    KPI - META
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        sm: 6,
                        md: 3
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95
                    }}

                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                    }}

                    transition={{
                        duration: 0.45,
                        delay: 0.25,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box

                                sx={{

                                    display: "flex",

                                    justifyContent:
                                        "space-between"

                                }}

                            >

                                <Box>

                                    <Typography

                                        color="text.secondary"

                                        fontSize={14}

                                        fontWeight={500}

                                    >

                                        Meta

                                    </Typography>


                                    <Typography

                                        variant="h4"

                                        fontWeight={700}

                                        sx={{
                                            mt: 1
                                        }}

                                    >

                                        {
                                            formatoMoneda(
                                                meta
                                            )
                                        }

                                    </Typography>

                                </Box>


                                <TrendingUpIcon

                                    sx={{

                                        fontSize: 34,

                                        color:
                                            "primary.main"

                                    }}

                                />

                            </Box>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                                sx={{
                                    mt: 2
                                }}

                            >

                                Objetivo de ahorro

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    KPI - PAGADOS
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        sm: 6,
                        md: 3
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95
                    }}

                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                    }}

                    transition={{
                        duration: 0.45,
                        delay: 0.35,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box

                                sx={{

                                    display: "flex",

                                    justifyContent:
                                        "space-between"

                                }}

                            >

                                <Box>

                                    <Typography

                                        color="text.secondary"

                                        fontSize={14}

                                        fontWeight={500}

                                    >

                                        Pagados

                                    </Typography>


                                    <Typography

                                        variant="h4"

                                        fontWeight={700}

                                        sx={{
                                            mt: 1
                                        }}

                                    >

                                        {pagados}

                                    </Typography>

                                </Box>


                                <CheckCircleIcon

                                    sx={{

                                        fontSize: 34,

                                        color:
                                            "success.main"

                                    }}

                                />

                            </Box>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                                sx={{
                                    mt: 2
                                }}

                            >

                                Abonos realizados

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    KPI - PENDIENTES
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        sm: 6,
                        md: 3
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40,
                        scale: 0.95
                    }}

                    animate={{
                        opacity: 1,
                        y: 0,
                        scale: 1
                    }}

                    transition={{
                        duration: 0.45,
                        delay: 0.45,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box

                                sx={{

                                    display: "flex",

                                    justifyContent:
                                        "space-between"

                                }}

                            >

                                <Box>

                                    <Typography

                                        color="text.secondary"

                                        fontSize={14}

                                        fontWeight={500}

                                    >

                                        Pendientes

                                    </Typography>


                                    <Typography

                                        variant="h4"

                                        fontWeight={700}

                                        sx={{
                                            mt: 1
                                        }}

                                    >

                                        {pendientes}

                                    </Typography>

                                </Box>


                                <PendingIcon

                                    sx={{

                                        fontSize: 34,

                                        color:
                                            "error.main"

                                    }}

                                />

                            </Box>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                                sx={{
                                    mt: 2
                                }}

                            >

                                Abonos restantes

                            </Typography>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    MONTO FINAL
                ================================================= */}

                <Grid

                    size={12}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 30
                    }}

                    animate={{
                        opacity: 1,
                        y: 0
                    }}

                    transition={{
                        duration: 0.6,
                        delay: 0.55,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={{

                            borderRadius: 3,

                            overflow: "hidden",

                            background:
                                "linear-gradient(135deg, #1B5E20, #43A047)",

                            color: "white",

                            boxShadow:
                                "0 8px 28px rgba(46,125,50,0.25)"

                        }}

                    >

                        <CardContent

                            sx={{

                                p: {
                                    xs: 3,
                                    md: 4
                                }

                            }}

                        >

                            <Box

                                sx={{

                                    display: "flex",

                                    flexDirection: {
                                        xs: "column",
                                        md: "row"
                                    },

                                    justifyContent:
                                        "space-between",

                                    gap: 3

                                }}

                            >

                                <Box>

                                    <Typography

                                        variant="body2"

                                        sx={{

                                            opacity: 0.85,

                                            mb: 1

                                        }}

                                    >

                                        Monto estimado al finalizar

                                    </Typography>


                                    <Typography

                                        variant="h3"

                                        fontWeight={800}

                                        sx={{

                                            fontSize: {
                                                xs: "2.2rem",
                                                md: "3rem"
                                            }

                                        }}

                                    >

                                        {
                                            formatoMoneda(
                                                totalFinal
                                            )
                                        }

                                    </Typography>

                                </Box>


                                <Box

                                    sx={{

                                        minWidth: {
                                            md: 300
                                        }

                                    }}

                                >

                                    <Box

                                        sx={{

                                            display: "flex",

                                            justifyContent:
                                                "space-between",

                                            mb: 1

                                        }}

                                    >

                                        <Typography>

                                            Ahorro aportado

                                        </Typography>


                                        <Typography
                                            fontWeight={700}
                                        >

                                            {
                                                formatoMoneda(
                                                    meta
                                                )
                                            }

                                        </Typography>

                                    </Box>


                                    <Box

                                        sx={{

                                            display: "flex",

                                            justifyContent:
                                                "space-between",

                                            mb: 1

                                        }}

                                    >

                                        <Typography>

                                            Rendimiento

                                        </Typography>


                                        <Typography
                                            fontWeight={700}
                                        >

                                            {
                                                formatoMoneda(
                                                    ganancia
                                                )
                                            }

                                        </Typography>

                                    </Box>


                                    <Divider

                                        sx={{

                                            borderColor:
                                                "rgba(255,255,255,0.25)",

                                            my: 1.5

                                        }}

                                    />


                                    <Typography

                                        variant="body2"

                                        sx={{
                                            opacity: 0.85
                                        }}

                                    >

                                        Rendimiento aplicado:{" "}

                                        <strong>

                                            {
                                                (
                                                    rendimiento * 100
                                                ).toFixed(0)
                                            }%

                                        </strong>

                                    </Typography>

                                </Box>

                            </Box>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    PROGRESO
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        md: 8
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        x: -50
                    }}

                    animate={{
                        opacity: 1,
                        x: 0
                    }}

                    transition={{
                        duration: 0.6,
                        delay: 0.65,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box
                                sx={{ mb: 2 }}
                            >

                                <Typography

                                    variant="h6"

                                    fontWeight={700}

                                >

                                    Progreso del ahorro

                                </Typography>


                                <Typography

                                    variant="body2"

                                    color="text.secondary"

                                >

                                    Evolución de tu ahorro acumulado

                                </Typography>

                            </Box>


                            <ResponsiveContainer

                                width="100%"

                                height={300}

                            >

                                <AreaChart
                                    data={progreso}
                                >

                                    <CartesianGrid

                                        strokeDasharray="3 3"

                                        vertical={false}

                                    />


                                    <XAxis

                                        dataKey="fecha"

                                        tick={{
                                            fontSize: 12
                                        }}

                                    />


                                    <YAxis

                                        tick={{
                                            fontSize: 12
                                        }}

                                    />


                                    <Tooltip />


                                    <Area

                                        type="monotone"

                                        dataKey="acumulado"

                                        stroke="#2e7d32"

                                        fill="#81c784"

                                        strokeWidth={3}

                                    />

                                </AreaChart>

                            </ResponsiveContainer>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    PIE
                ================================================= */}

                <Grid

                    size={{
                        xs: 12,
                        md: 4
                    }}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        x: 50
                    }}

                    animate={{
                        opacity: 1,
                        x: 0
                    }}

                    transition={{
                        duration: 0.6,
                        delay: 0.75,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Typography

                                variant="h6"

                                fontWeight={700}

                            >

                                Estado de abonos

                            </Typography>


                            <Typography

                                variant="body2"

                                color="text.secondary"

                            >

                                Abonos realizados vs pendientes

                            </Typography>


                            <ResponsiveContainer

                                width="100%"

                                height={270}

                            >

                                <PieChart>

                                    <Pie

                                        data={pie}

                                        dataKey="value"

                                        cx="50%"

                                        cy="50%"

                                        innerRadius={65}

                                        outerRadius={95}

                                        paddingAngle={3}

                                    >

                                        {

                                            pie.map(
                                                (entry, index) => (

                                                    <Cell

                                                        key={index}

                                                        fill={
                                                            COLORS[index]
                                                        }

                                                    />

                                                )
                                            )

                                        }

                                    </Pie>


                                    <Tooltip />

                                </PieChart>

                            </ResponsiveContainer>


                            <Box

                                sx={{

                                    display: "flex",

                                    justifyContent:
                                        "center",

                                    gap: 2,

                                    flexWrap:
                                        "wrap"

                                }}

                            >

                                <Typography

                                    variant="body2"

                                    color="success.main"

                                    fontWeight={600}

                                >

                                    ● Pagados: {pagados}

                                </Typography>


                                <Typography

                                    variant="body2"

                                    color="error.main"

                                    fontWeight={600}

                                >

                                    ● Pendientes: {pendientes}

                                </Typography>

                            </Box>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    BARRAS
                ================================================= */}

                <Grid

                    size={12}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40
                    }}

                    animate={{
                        opacity: 1,
                        y: 0
                    }}

                    transition={{
                        duration: 0.6,
                        delay: 0.85,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box
                                sx={{ mb: 2 }}
                            >

                                <Typography

                                    variant="h6"

                                    fontWeight={700}

                                >

                                    Abonos programados

                                </Typography>


                                <Typography

                                    variant="body2"

                                    color="text.secondary"

                                >

                                    Consulta el monto de cada aportación

                                </Typography>

                            </Box>


                            <ResponsiveContainer

                                width="100%"

                                height={300}

                            >

                                <BarChart
                                    data={datos}
                                >

                                    <CartesianGrid

                                        strokeDasharray="3 3"

                                        vertical={false}

                                    />


                                    <XAxis

                                        dataKey="fecha"

                                        tick={{
                                            fontSize: 12
                                        }}

                                    />


                                    <YAxis

                                        tick={{
                                            fontSize: 12
                                        }}

                                    />


                                    <Tooltip />


                                    <Bar

                                        dataKey="cantidad"

                                        radius={[
                                            6,
                                            6,
                                            0,
                                            0
                                        ]}

                                    >

                                        {

                                            datos.map(
                                                (item, index) => (

                                                    <Cell

                                                        key={index}

                                                        fill={
                                                            item.ahorro_pagado
                                                                ? "#4caf50"
                                                                : "#ef5350"
                                                        }

                                                    />

                                                )
                                            )

                                        }

                                    </Bar>

                                </BarChart>

                            </ResponsiveContainer>

                        </CardContent>

                    </Card>

                </Grid>


                {/* =================================================
                    TABLA
                ================================================= */}

                <Grid

                    size={12}

                    component={motion.div}

                    initial={{
                        opacity: 0,
                        y: 40
                    }}

                    animate={{
                        opacity: 1,
                        y: 0
                    }}

                    transition={{
                        duration: 0.6,
                        delay: 0.95,
                        easing: "ease-out"
                    }}

                >

                    <Card

                        component={motion.div}

                        whileHover={{
                            y: -6,
                            scale: 1.02
                        }}

                        transition={{
                            duration: 0.2
                        }}

                        sx={cardSX}

                    >

                        <CardContent
                            sx={{ p: 3 }}
                        >

                            <Box
                                sx={{ mb: 2 }}
                            >

                                <Typography

                                    variant="h6"

                                    fontWeight={700}

                                >

                                    Seguimiento de abonos

                                </Typography>


                                <Typography

                                    variant="body2"

                                    color="text.secondary"

                                >

                                    Historial de aportaciones

                                </Typography>

                            </Box>


                            <TableContainer

                                component={Paper}

                                elevation={0}

                                sx={{

                                    border:
                                        "1px solid",

                                    borderColor:
                                        "divider",

                                    borderRadius: 2,

                                    overflow:
                                        "hidden"

                                }}

                            >

                                <Table>


                                    <TableHead>

                                        <TableRow

                                            sx={{

                                                backgroundColor:
                                                    "#f5f7fb"

                                            }}

                                        >

                                            <TableCell

                                                sx={{
                                                    fontWeight: 700
                                                }}

                                            >

                                                Fecha

                                            </TableCell>


                                            <TableCell

                                                sx={{
                                                    fontWeight: 700
                                                }}

                                            >

                                                Cantidad

                                            </TableCell>


                                            <TableCell

                                                sx={{
                                                    fontWeight: 700
                                                }}

                                            >

                                                Estado

                                            </TableCell>

                                        </TableRow>

                                    </TableHead>


                                    <TableBody>

                                        {

                                            datos.map(
                                                item => (

                                                    <TableRow

                                                        key={
                                                            item.id_ahorro
                                                        }

                                                        hover

                                                    >

                                                        <TableCell>

                                                            {
                                                                item.fecha
                                                            }

                                                        </TableCell>


                                                        <TableCell

                                                            sx={{
                                                                fontWeight: 600
                                                            }}

                                                        >

                                                            {
                                                                formatoMoneda(
                                                                    item.cantidad
                                                                )
                                                            }

                                                        </TableCell>


                                                        <TableCell>

                                                            <Chip

                                                                icon={

                                                                    item.ahorro_pagado

                                                                        ? (
                                                                            <CheckCircleIcon />
                                                                        )

                                                                        : (
                                                                            <PendingIcon />
                                                                        )

                                                                }

                                                                label={

                                                                    item.ahorro_pagado

                                                                        ? "Pagado"

                                                                        : "Pendiente"

                                                                }

                                                                color={

                                                                    item.ahorro_pagado

                                                                        ? "success"

                                                                        : "error"

                                                                }

                                                                variant="outlined"

                                                                size="small"

                                                            />

                                                        </TableCell>

                                                    </TableRow>

                                                )
                                            )

                                        }

                                    </TableBody>

                                </Table>

                            </TableContainer>

                        </CardContent>

                    </Card>

                </Grid>


            </Grid>

        </Box>

    );

};


export default GraficosSeguimientos;